import React, { useState } from "react";
import { ChevronDown, List, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { ago } from "@/lib/sk";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StickyHead, KeylineRow } from "@/components/canvas/StickyHead";
import { VersionRow, versionMeta, sumText } from "@/components/canvas/tilbud/VersionRail";
import { TOC_DOT, scrollToSection } from "@/components/canvas/QuoteToc";

const quiet = "inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13.5px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

/** «Utkast ▾» / «Versjon 2 · Kontrollert ▾» – the state word is also where versions are chosen. */
export function StateWord({ sel, revisions, current, liveTotals, onPick }) {
  const [open, setOpen] = useState(false);
  const label = sel === "live" ? "Utkast" : `Versjon ${current?.revision}${current?.status === "kontrollert" ? " · Kontrollert" : ""}`;
  const pick = (id) => { setOpen(false); onPick(id); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[7px] px-1.5 text-[15px] text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground data-[state=open]:bg-black/[0.05]" aria-label="Versjoner" data-testid="tilbud-view-picker">
          {current?.status === "kontrollert" ? <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--v-ok))]" /> : null}{label} <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-[360px] max-w-[calc(100vw-24px)] rounded-2xl border-black/[0.06] p-1.5 shadow-float" data-testid="tilbud-versions">
        <ol className="space-y-px">
          <VersionRow live active={sel === "live"} liveTotals={liveTotals} onPick={() => pick("live")} testId="tilbud-view-live" />
          {revisions.map((r) => <VersionRow key={r.id} r={r} active={sel === r.id} onPick={() => pick(r.id)} testId={`tilbud-view-rev-${r.revision}`} short />)}
        </ol>
        {!revisions.length ? <p className="px-3 py-2 text-[12.5px] text-muted-foreground">Ingen versjoner ennå.</p> : null}
      </PopoverContent>
    </Popover>
  );
}

/** «Innhold ▾» for surfaces too narrow for the rail – the same sections, the same marks. */
export function SectionsMenu({ sections, paperId = "tilbud-paper" }) {
  const [open, setOpen] = useState(false);
  if (!sections?.length) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(quiet, "px-2.5")} aria-label="Innhold i tilbudet" data-testid="tilbud-sections-menu"><List className="h-4 w-4" /> <span className="max-sm:hidden">Innhold</span></button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-[260px] rounded-2xl border-black/[0.06] p-1.5 shadow-float" data-testid="tilbud-sections-list">
        {sections.map((s) => (
          <button key={s.key} type="button" onClick={() => { setOpen(false); scrollToSection(s.key, paperId); }} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[13px] hover:bg-black/[0.04]" data-testid={`tilbud-sections-menu-${s.key}`} data-tone={s.tone}>
            <span className="truncate">{s.label}</span>
            {TOC_DOT[s.tone] ? <span className={cn("toc-dot ml-auto", TOC_DOT[s.tone])} aria-hidden /> : null}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** One line that can be scanned: the sum, what is in it, and where this version comes from. Never 0 for the unknown. */
export function Keyline({ vm, live, current, lastChange }) {
  const t = vm?.totals || {};
  const posts = (vm?.lines || []).length;
  const opts = (vm?.options || []).length;
  const missing = [t.missing_count ? `${t.missing_count} ${t.missing_count === 1 ? "post" : "poster"} uten pris` : null, t.pending_count ? `${t.pending_count} ikke ferdig vurdert` : null].filter(Boolean).join(", ");
  const meta = live ? (lastChange?.at ? `Sist endret ${ago(lastChange.at)}${lastChange.by?.name ? ` av ${lastChange.by.name}` : ""}` : "Følger kalkylen") : versionMeta(current);
  return (
    <KeylineRow testId="tilbud-keyline">
      <span data-testid="tilbud-keyline-sum" data-preliminary={t.preliminary ? "1" : "0"}>Sum eks. mva <span className={cn("font-semibold tabular-nums", t.preliminary ? "text-[hsl(var(--v-assumed))]" : "text-foreground")}>{sumText(t.price, t.preliminary)}</span>{t.preliminary ? <span className="text-[hsl(var(--v-assumed))]" data-testid="tilbud-keyline-preliminary"> foreløpig{missing ? ` – ${missing}` : ""}</span> : null}</span>
      <span data-testid="tilbud-keyline-posts">{posts} {posts === 1 ? "post" : "poster"}</span>
      {opts ? <span data-testid="tilbud-keyline-options">{opts} {opts === 1 ? "opsjon" : "opsjoner"} utenfor sum</span> : null}
      {meta ? <span data-testid="tilbud-keyline-meta">{meta}</span> : null}
    </KeylineRow>
  );
}

/** The Tilbud head: «Tilbud 26077» + state word, the mode switch and the one highlighted action – on the shared sticky model. */
export function TilbudHead({ headRef, compact, project, stateWord, subtitle, controls, keyline, note, maxWidth }) {
  return <StickyHead headRef={headRef} compact={compact} title={<>Tilbud {project.number}</>} aside={stateWord} subtitle={subtitle} controls={controls} keyline={keyline} note={note} maxWidth={maxWidth} className="bg-[hsl(210_24%_98%)]/85" testId="tilbud-toolbar" titleTestId="tilbud-header" noteTestId="tilbud-status-line" />;
}
